import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../next/app/auth/authHooks';
import { useSignOut } from '../next/app/auth/useSignOut';

/**
 * GateLayout - Minimal shell for the signed-in-but-not-yet-allowlisted state.
 *
 * Per access-control cross-cutting decisions D3 + R21:
 * - Brand mark top-left, NO nav links (a signed-in not-yet-allowlisted user
 *   should not see "Log in" / "Demo" marketing CTAs, nor the app Sidebar /
 *   ProgramSwitcher chrome).
 * - Signed-in user's avatar + email top-right (so they know auth succeeded),
 *   with a plain-text "Sign out" link beneath it wired to the auth context.
 * - A single centered card region that renders arbitrary `children`:
 *   max-width 480px on desktop, full width with a 24px horizontal gutter on
 *   mobile (<640px), vertically centered on a min-height-100vh flex container.
 *
 * This is a presentational shell ONLY. It does NOT contain the /access-pending
 * form (that ships in a later PR); it works with any children.
 *
 * Sibling layouts:
 * - PublicLayout  — marketing chrome (Log in / Demo) for anonymous users.
 * - GlobalLayout  — full authenticated app shell (Sidebar + ProgramSwitcher).
 * - GateLayout    — this; authenticated-but-not-yet-allowlisted.
 */
export default function GateLayout({ children }) {
    // Sign-out MUST be Auth0-aware: a local-only logout leaves the Auth0 SSO session
    // intact, so /login silently re-auths a Google user back to /access-pending (the
    // login form never shows). useSignOut clears Auth0 too. See next/app/auth/useSignOut.
    const { user } = useAuth();
    const signOut = useSignOut();

    const email = user?.email ?? '';
    const verifiedName = user?.name === 'Loading...' ? '' : (user?.name ?? '');
    // A token proves a session candidate, not a profile. Use a neutral marker
    // until the backend supplies a verified email or name.
    const initial = (email || verifiedName).trim().charAt(0).toUpperCase() || '?';
    const identityLabel = email || verifiedName || 'Signed-in account';

    return (
        <div className="min-h-screen flex flex-col bg-background">
            {/* Top bar: brand left, identity right. No nav links. */}
            <header className="flex items-start justify-between px-6 py-4">
                {/* Brand mark — matches PublicHeader wordmark, but not a nav link cluster. */}
                <a href="/" className="text-xl font-bold text-foreground">
                    Pinpoint Edu
                </a>

                {/* Signed-in identity: avatar + email, with Sign out beneath. */}
                {user && (
                    <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                                {user.picture && (
                                    <AvatarImage src={user.picture} alt={email || 'User avatar'} />
                                )}
                                <AvatarFallback className="text-xs">{initial}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-foreground max-w-[12rem] truncate">
                                {identityLabel}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={signOut}
                            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                        >
                            Sign out
                        </button>
                    </div>
                )}
            </header>

            {/* Centered card region. Vertically centered via flex on the
                min-height-100vh container; the gutter (px-6 = 24px) supplies the
                mobile horizontal margin, and max-w-[480px] caps desktop width. */}
            <main className="flex-1 flex items-center justify-center px-6 pb-16">
                <div className="w-full max-w-[480px]">
                    {children}
                </div>
            </main>
        </div>
    );
}
